import React, { useEffect, useState, useMemo } from 'react'
import Select from 'react-select'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'
import BasicModal from '@/components/UI/Dialog/Dialog'
import DragAndDrop from '@/components/UI/DragAndDrop/DragAndDrop'
import dropDownBlue from '@/assets/New-images/dropdown-arow-blue.svg'
import ApiUtil from '@/utils/apiUtil'
import { getAgentFromStorage } from '@/utils/appUtils'
import { HelperService } from '@/services/helper.service'
import AvatarPreview from '../AvatarPreview/AvatarPreview'
import glbimg from '@/assets/images/file-glb.svg'
import closeimg from '@/assets/New-images/close-icon-red.svg'
import { Image } from 'react-bootstrap'

interface AvatarPickerProps {
  agentId?: string
  agentSettings?: any
  disabled?: boolean
  onChange: (partial: { avatar_id: string }) => void
  selectedAvatarId?: string
}

interface AvatarOption {
  _id: string
  url: string
  name: string
  skeleton_type?: string
  is_system?: boolean
  source_avatar_id?: string
}

const AvatarPicker: React.FC<AvatarPickerProps> = ({
  agentId,
  agentSettings,
  disabled,
  onChange,
  selectedAvatarId: selectedAvatarIdProp,
}) => {
  const { t } = useTranslation('translation')
  const helperService = new HelperService()

  const [isLoading, setIsLoading] = useState(true)
  const [avatarOptions, setAvatarOptions] = useState<AvatarOption[]>([])
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>(
    selectedAvatarIdProp || '',
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tab, setTab] = useState<'file' | 'url'>('url')
  const [url, setUrl] = useState('')
  const [files, setFiles] = useState<any[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingAvatars, setIsLoadingAvatars] = useState(false)
  const [backgroundUrl, setBackgroundUrl] = useState<string>('')
  const [urlError, setUrlError] = useState<string>('')
  const [uploadError, setUploadError] = useState<string>('')

  // RPM URL pattern
  const RPM_URL_PATTERN = /^https:\/\/models\.readyplayer\.me\/.*\.glb/i
  const validateRPMUrl = (urlValue: string): boolean => {
    if (!urlValue || urlValue.trim() === '') {
      setUrlError('')
      return false
    }
    const isValid = RPM_URL_PATTERN.test(urlValue)
    if (!isValid) {
      setUrlError(
        t(
          'Please enter a valid ReadyPlayerMe avatar URL (e.g., https://models.readyplayer.me/...glb)',
        ) as string,
      )
    } else setUrlError('')
    return isValid
  }

  const getAvatarsList = async (currentAgentId?: string) => {
    try {
      setIsLoadingAvatars(true)
      const fetchAgentId = currentAgentId || agentId || getAgentFromStorage()
      const res = await ApiUtil.SendAsync<any>({
        url: `agentavatar/agents/${fetchAgentId}/avatars/list`,
        method: 'GET',
      })
      if (res.status === 200 && res.data?.data?.avatars) {
        setAvatarOptions(res.data.data.avatars)
      }
    } catch (error) {
      console.error('Failed to fetch avatars:', error)
    } finally {
      setIsLoadingAvatars(false)
    }
  }

  const getAgentSettings = async () => {
    try {
      const currentAgentId = agentId || getAgentFromStorage()
      const res = await ApiUtil.SendAsync<any>({
        url: `knowledge/agents/current?agent_id=${currentAgentId}`,
        method: 'GET',
      })
      if (res.status === 200 && res.data?.data?.agent) {
        await getAvatarsList(currentAgentId)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const uploadAgentAvatar = async (formData: FormData, agentId?: string) => {
    return await ApiUtil.SendAsync<any>({
      url: `agentavatar/agents/${agentId ? agentId : getAgentFromStorage()}/avatars/upload`,
      method: 'POST',
      data: formData,
    })
  }

  const setAgentAvatar = async (agentavatarId: string) => {
    try {
      if (selectedAvatarId === agentavatarId) return true
      setSelectedAvatarId(agentavatarId)
      onChange({ avatar_id: agentavatarId })
      return true
    } catch (error) {
      console.error('Failed to set avatar:', error)
      return false
    }
  }

  useEffect(() => {
    getAgentSettings()
  }, [agentId])

  useEffect(() => {
    if (selectedAvatarIdProp && selectedAvatarIdProp !== selectedAvatarId) {
      setSelectedAvatarId(selectedAvatarIdProp)
    }
  }, [selectedAvatarIdProp])

  useEffect(() => {
    if (!avatarOptions || avatarOptions.length === 0) return
    const defaultAvatar = avatarOptions.find(
      a =>
        a.is_system === true &&
        a.source_avatar_id === 'system_avatar_male__001',
    )
    const first = avatarOptions[0]
    const nextId = selectedAvatarIdProp || defaultAvatar?._id || first._id
    setSelectedAvatarId(prev => (prev ? prev : nextId))
  }, [avatarOptions, selectedAvatarIdProp])

  useEffect(() => {
    const customBg = agentSettings?.settings?.custom_avatar_background_url
    const agentBg = agentSettings?.settings?.avatar_background_url
    const computed = (customBg || agentBg || '').trim()
    setBackgroundUrl(prev => (prev !== computed ? computed : prev))
  }, [agentSettings])

  const selectOptions = useMemo(
    () =>
      avatarOptions.map((avatar, index) => {
        let label: string
        if (avatar.is_system === false) {
          const customIdx = avatarOptions
            .slice(0, index + 1)
            .filter(a => a.is_system === false).length
          label = `Custom Avatar ${customIdx}`
        } else {
          label = avatar.name || `Avatar - ${avatar.skeleton_type || 'unknown'}`
        }
        return { value: avatar._id, label }
      }),
    [avatarOptions],
  )

  const currentPreviewUrl = useMemo(() => {
    if (!avatarOptions || avatarOptions.length === 0) return ''
    const found =
      avatarOptions.find(a => a._id === selectedAvatarId) || avatarOptions[0]
    return found.url
  }, [avatarOptions, selectedAvatarId])

  const onUploadByUrl = async () => {
    if (!url || disabled) return
    if (!validateRPMUrl(url)) return

    const fd = new FormData()
    fd.append('url', url)
    try {
      setIsUploading(true)
      setUploadError('')
      const response = await uploadAgentAvatar(fd)
      if (response?.status === 200 && response?.data?.data?.avatar) {
        const newAvatar = response.data.data.avatar
        setAvatarOptions(prev => [...prev, newAvatar])
        await setAgentAvatar(newAvatar._id)
        setUrl('')
        setUrlError('')
        setFiles([])
        setIsModalOpen(false)
        helperService.showToaster(t('Custom Avatar Uploaded'), 'success')
      }
    } catch (error: any) {
      console.error('Failed to upload avatar:', error)
      setUploadError(
        error?.response?.data?.message ||
          t(
            'Failed to upload avatar. Please check the file format and try again.',
          ),
      )
    } finally {
      setIsUploading(false)
    }
  }

  const onUploadByFile = async () => {
    const file = files?.[0]
    if (!file || disabled) return
    const fd = new FormData()
    fd.append('file', file, file.name)
    try {
      setIsUploading(true)
      setUploadError('')
      const response = await uploadAgentAvatar(fd)
      if (response?.status === 200 && response?.data?.data?.avatar) {
        const newAvatar = response.data.data.avatar
        setAvatarOptions(prev => [...prev, newAvatar])
        await setAgentAvatar(newAvatar._id)
        setFiles([])
        setUrl('')
        setUrlError('')
        setIsModalOpen(false)
        helperService.showToaster(t('Custom Avatar Uploaded'), 'success')
      }
    } catch (error: any) {
      console.error('Failed to upload avatar:', error)
      setUploadError(
        error?.response?.data?.message ||
          t(
            'Failed to upload avatar. Please check the file format and try again.',
          ),
      )
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div>
      <div className="tw-flex tw-gap-4">
        {/* Left - select */}
        <div className="tw-w-1/2 tw-space-y-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex-1">
              <Select
                className="tw-w-full tw-border tw-border-[#8F94A3] tw-rounded-md tw-outline-none"
                classNamePrefix="select-target-prefix"
                isDisabled={!!disabled || isLoadingAvatars}
                components={{
                  DropdownIndicator: () => (
                    <img className="mr-2" src={dropDownBlue} alt="dropdown" />
                  ),
                  IndicatorSeparator: () => null,
                }}
                options={selectOptions}
                value={
                  selectOptions.find(o => o.value === selectedAvatarId) || null
                }
                onChange={(opt: any) => {
                  if (disabled || isLoadingAvatars) return
                  if (opt?.value) setAgentAvatar(opt.value)
                }}
              />
            </div>
            <Button
              variant="outlined"
              color="primary"
              disabled={!!disabled}
              onClick={() => !disabled && setIsModalOpen(true)}
            >
              {t('Add Custom Avatar')}
            </Button>
          </div>
        </div>

        {/* Right - preview */}
        <div className="tw-w-1/2">
          <div className="tw-rounded-lg tw-border tw-border-[#959296] tw-overflow-hidden tw-h-[320px] tw-relative">
            {isLoading ? (
              <div className="tw-text-base tw-h-full tw-flex tw-items-center tw-justify-center">
                {t('Loading avatar...')}
              </div>
            ) : currentPreviewUrl ? (
              <div className="tw-w-full tw-h-full tw-isolate">
                <AvatarPreview
                  previewUrl={currentPreviewUrl}
                  backgroundColor={0xfafafa}
                  backgroundImageUrl={backgroundUrl || undefined}
                  enableControls={false}
                  pixelRatio={3}
                />
              </div>
            ) : (
              <div className="tw-text-base tw-h-full tw-flex tw-items-center tw-justify-center">
                {t('No avatar selected')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <BasicModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setUploadError('')
          }}
          title={t('Add Custom Avatar') as string}
          sx={{ width: '720px' }}
        >
          <div className="tw-space-y-4">
            <div>
              <h4 className="tw-text-base tw-font-semibold">
                {t('Step 1: Create Your 3D Avatar')}
              </h4>
              <p className="tw-text-base">
                {t('Create your custom 3D avatar with')}{' '}
                <a
                  href="https://readyplayer.me/avatar"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-[#001DE0]"
                >
                  {t('Ready Player Me')}
                </a>
              </p>
            </div>

            <div>
              <h4 className="tw-text-base tw-font-semibold">
                {t('Step 2: Import 3D Avatar')}
              </h4>
              <p className="tw-text-base">
                {t(
                  'Once you have created your avatar, either upload the .glb file or paste the URL below.',
                )}{' '}
                <a
                  href="https://noodlefactorysupport.super.site/how-to-properly-export-your-3d-avatar-from-ready-player-me"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-[#001DE0] tw-underline"
                >
                  {t('Learn about how to import 3D Avatar here')}
                </a>
              </p>
            </div>

            <div className="tw-flex tw-rounded-[28px] tw-overflow-hidden tw-border tw-border-[#001DE0]">
              <button
                type="button"
                onClick={() => setTab('file')}
                className={
                  tab === 'file'
                    ? 'tw-bg-[#001DE0] tw-text-white tw-flex-1 tw-py-3'
                    : 'tw-bg-white tw-text-[#001DE0] tw-flex-1 tw-py-3'
                }
              >
                {t('Upload 3D Avatar')}
              </button>
              <button
                type="button"
                onClick={() => setTab('url')}
                className={
                  tab === 'url'
                    ? 'tw-bg-[#001DE0] tw-text-white tw-flex-1 tw-py-3'
                    : 'tw-bg-white tw-text-[#001DE0] tw-flex-1 tw-py-3'
                }
              >
                {t('Import from URL')}
              </button>
            </div>

            {tab === 'url' && (
              <div className="tw-space-y-2">
                <input
                  className={`tw-w-full tw-rounded-md tw-outline-none tw-px-4 tw-py-3 ${
                    urlError
                      ? 'tw-border tw-border-red-500'
                      : 'tw-border tw-border-[#8F94A3]'
                  }`}
                  placeholder={
                    t('https://models.readyplayer.me/...glb') as string
                  }
                  value={url}
                  onChange={e => {
                    setUrl(e.target.value)
                    validateRPMUrl(e.target.value)
                  }}
                />
                {urlError && (
                  <div className="tw-text-red-500 tw-text-sm">{urlError}</div>
                )}
                {uploadError && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-2">
                    {t(
                      'Failed to upload avatar. Please check the file format and try again.',
                    )}
                  </div>
                )}

                <div className="tw-text-right">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={
                      isUploading || !url || url.trim() === '' || !!urlError
                    }
                    onClick={onUploadByUrl}
                  >
                    {isUploading ? t('Uploading') : t('Upload Avatar')}
                  </Button>
                </div>
              </div>
            )}

            {tab === 'file' && (
              <div className="tw-space-y-3">
                <div className="tw-bg-[#ECEFF7] tw-rounded-md tw-border tw-border-[#C5D0E0] tw-p-4">
                  <DragAndDrop
                    getFileDocumentsHandler={files => {
                      setFiles(files)
                      setUploadError('') // Clear error when selecting new files
                    }}
                    supportedTypes={'.glb'}
                    maxFiles={1}
                    message={
                      "<h2 class='mb-1'>" +
                      t('Drop your files here or click to browse.') +
                      '</h2><div>' +
                      t('Supported File types') +
                      ': .glb</div>'
                    }
                    fileSizeMessage={t('You can upload only single file.')}
                    className={'!tw-bg-[#ECEFF7] !tw-border-none !tw-w-full'}
                  />
                </div>
                {(!files || files.length === 0 || uploadError) && (
                  <div
                    className="tw-flex tw-items-center tw-gap-3 tw-p-4
                   tw-bg-[#FFF3C6] tw-border tw-border-[#F5B800]
                   tw-rounded-lg tw-w-full tw-text-left"
                  >
                    <div
                      className="tw-flex tw-items-center tw-justify-center
                     tw-w-5 tw-h-5 tw-rounded-full
                     tw-bg-[#F5B800] tw-text-white tw-font-bold tw-text-xs
                     tw-leading-none tw-flex-shrink-0 tw-mt-[1px]"
                    >
                      !
                    </div>
                    <div className="tw-flex-1">
                      <p className="tw-text-[15px] tw-leading-relaxed tw-text-[#0B1242] tw-m-0">
                        {t(
                          'Some custom 3D avatars may have compatibility issues depending on how they were made.',
                        )}{' '}
                        <a
                          href="https://noodlefactorysupport.super.site/troubleshooting/understanding-3d-avatar-compatibility"
                          target="_blank"
                          rel="noreferrer"
                          className="tw-text-[#001DE0] tw-underline tw-text-[15px] tw-leading-relaxed"
                        >
                          {t('Learn what to check before using one.')}
                        </a>
                      </p>
                    </div>
                  </div>
                )}
                {uploadError && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-2">
                    {t(
                      'Failed to upload avatar. Please check the file format and try again.',
                    )}
                  </div>
                )}
                {files && files.length > 0 && (
                  <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-[#C5D0E0] tw-rounded-md tw-p-3 tw-mt-2">
                    <img
                      src={glbimg}
                      alt="glb"
                      className="tw-w-6 tw-h-6 tw-mr-3"
                    />
                    <div className="tw-flex-1 tw-min-w-0">
                      <div
                        className="tw-font-medium tw-text-base tw-truncate"
                        title={files[0].name}
                      >
                        {files[0].name}
                      </div>
                      <div className="tw-text-xs tw-text-gray-500">
                        {(files[0].size / 1024).toFixed(3)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn p-0 ml-3 btn-red-close"
                      onClick={() => {
                        setFiles([])
                        setUploadError('')
                      }}
                      aria-label="Remove file"
                    >
                      <Image
                        src={closeimg}
                        alt="remove"
                        width={20}
                        height={20}
                      />
                    </button>
                  </div>
                )}
                <div className="tw-text-right">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={isUploading || !files || files.length === 0}
                    onClick={onUploadByFile}
                  >
                    {isUploading ? t('Uploading') : t('Upload Avatar')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </BasicModal>
      )}
    </div>
  )
}

export default AvatarPicker
